import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar,
} from '@mui/material';
import { Field } from '../../UI';
import {
  buscarEmailsPadraoDevedorAcordo, buscarParametrosEmailTermoAcordo, enviarEmailsTermoAcordo,
} from '../../../../services/core/processos/processosAcordosService';
import { isBadRequestException, haveExceptionValidations } from '../../../../helpers/utils/utils';
import { makeErrorField, updateFieldsWithErrors, extractResponseErrorMessage } from '../../../../helpers/utils/form.utils';
import useSessaoDialogMutacao from '../../../hooks/useSessaoDialogMutacao';

const MENSAGEM_ERRO_GENERICA = 'Não foi possível enviar o e-mail. Tente novamente.';
const MENSAGEM_EMAIL_INVALIDO = 'Informe um ou mais e-mails válidos, separados por vírgula ou ponto-e-vírgula.';
const PLACEHOLDER_EMAIL_LIST = 'email1@dominio.com, email2@dominio.com';

// Mesmo padrão de e-mail já usado em produção (`helpers/validators/validators.js`,
// `isValidEmailField`) — reaproveitado aqui como regra de validação de
// entrada (requisito de segurança do plano da Etapa 8), em vez de duplicar
// só a regra: `isValidEmailField` valida um único `FieldModel`, este campo
// aceita MÚLTIPLOS e-mails separados por vírgula/ponto-e-vírgula, então não
// serve para reuso direto. Ancorado (`^`/`$`) — o padrão original é o regex
// WHATWG do atributo HTML5 `pattern` (ancorado implicitamente pelo browser);
// usado cru com `.test()` em JS sem ancoragem, aceitaria qualquer string que
// contivesse um e-mail válido como SUBSTRING (ex.: `"foo@bar.com)"` ou lixo
// ao redor), invalidando o próprio propósito da checagem — achado em code
// review.
const EMAIL_REGEX = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

function normalizarEmails(emailList) {
  return emailList.split(/[,;]/).map(email => email.trim()).filter(Boolean);
}

/**
 * Dialog "Envelope" (Enviar Termo de Acordo por E-mail, AW2-1133 Etapa 8) —
 * port de `DialogEnviarEmail.jsx` real. Divergência confirmada por leitura
 * direta do código-fonte real em relação ao texto do plano de arquitetura
 * (que descrevia `buscarParametrosEmailTermoAcordo` como responsável por
 * popular o valor default): o default do campo "Para" vem de
 * `buscarEmailsPadraoDevedorAcordo` (fetch no `useEffect` de abertura);
 * `buscarParametrosEmailTermoAcordo` só é usado no SUBMIT, para montar o
 * payload que `enviarEmailsTermoAcordo(payload, 'completo')` de fato envia —
 * mesmo raciocínio já documentado em `src/prototipo/CLAUDE.md` ("não
 * assumir comportamento sem ler o código-fonte real").
 *
 * Fecha só no SUCESSO (`onClose()` dentro do `try`) — diferente das 3
 * variantes da Etapa 7 (que fecham incondicionalmente no `finally`): a tela
 * real (`onClickSubmitEmail`) só fecha após sucesso, mantendo o dialog
 * aberto em caso de erro para o usuário corrigir o(s) e-mail(s) sem perder o
 * que já digitou.
 *
 * Erro do backend SEM `validations` mapeáveis (regra de negócio bloqueante,
 * ex.: boleto já registrado) usa um dialog dedicado de confirmação
 * ("Ok, ENTENDI"), replicando `openDialogErroBoletoRegistrado`/
 * `DialogNew` real — não um Snackbar genérico, que desapareceria sem o
 * usuário necessariamente ler a mensagem de bloqueio.
 */
export default function DialogEnviarEmailAcordo({
  open, acordo, idProcesso, onClose, onMutado,
}) {
  const [emailList, setEmailList] = useState('');
  const [errorFields, setErrorFields] = useState({ emailList: makeErrorField() });
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [resultado, setResultado] = useState(null);
  const [dialogErro, setDialogErro] = useState({ aberto: false, mensagem: '' });
  // Guarda contra a resposta do default sobrescrever texto que o usuário já
  // começou a digitar antes dela chegar (achado em code review) — setado no
  // 1º `onChange`, resetado só na abertura real (`aoAbrir`, abaixo).
  const editadoRef = useRef(false);

  const { sessaoRef, enviandoRef } = useSessaoDialogMutacao(open, idProcesso, () => {
    editadoRef.current = false;
    setEmailList('');
    setErrorFields({ emailList: makeErrorField() });
    setEnviando(false);
    setErro('');
    setResultado(null);
    setDialogErro({ aberto: false, mensagem: '' });
  });

  useEffect(() => {
    if (!open || !acordo) {
      return;
    }
    const sessaoAlvo = sessaoRef.current;
    buscarEmailsPadraoDevedorAcordo(acordo.id).then(({ data }) => {
      if (sessaoRef.current === sessaoAlvo && !editadoRef.current) {
        setEmailList(data || '');
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, acordo?.id]);

  const onChangeEmailList = (e) => {
    editadoRef.current = true;
    setEmailList(e.target.value);
    setErrorFields({ emailList: makeErrorField() });
  };

  const onFecharResultado = () => setResultado(null);
  const onFecharErro = () => setErro('');
  const onFecharDialogErro = () => setDialogErro({ aberto: false, mensagem: '' });

  const onSubmit = async () => {
    if (enviandoRef.current || !acordo) {
      return;
    }

    const emails = normalizarEmails(emailList);
    if (emails.length === 0 || emails.some(email => !EMAIL_REGEX.test(email))) {
      setErrorFields({ emailList: { error: true, errorMessage: MENSAGEM_EMAIL_INVALIDO } });
      return;
    }

    const sessaoAlvo = sessaoRef.current;
    enviandoRef.current = true;
    setEnviando(true);
    setErrorFields({ emailList: makeErrorField() });
    setErro('');

    try {
      const { data } = await buscarParametrosEmailTermoAcordo(acordo.id, { emailList, nroParcela: null });
      await enviarEmailsTermoAcordo(data, 'completo');
      if (sessaoRef.current === sessaoAlvo) {
        onClose();
        setResultado({ severidade: 'success', mensagem: 'E-mail enviado com sucesso.' });
        // Porta `updatePage()` real — chamado só no SUCESSO
        // (`onClickSubmitEmail`), diferente do `finally` incondicional das 3
        // variantes da Etapa 7 (que mudam o status do acordo de fato).
        // Enviar e-mail não muda status/dado do acordo — só recarrega a
        // grid/cards por paridade com o comportamento real.
        if (onMutado) {
          onMutado();
        }
      }
    } catch (exception) {
      if (sessaoRef.current === sessaoAlvo) {
        if (isBadRequestException(exception) && haveExceptionValidations(exception)) {
          const [atualizados, naoMapeados] = updateFieldsWithErrors(exception, { emailList: makeErrorField() });
          const temErroVisivel = Object.values(atualizados).some(campo => campo.error);
          if (temErroVisivel) {
            setErrorFields(atualizados);
            if (naoMapeados.length > 0) {
              setErro(naoMapeados.map(item => item.message).join(' '));
            }
          } else {
            setErro(extractResponseErrorMessage(exception) || MENSAGEM_ERRO_GENERICA);
          }
        } else if (exception?.response?.data?.message) {
          setDialogErro({ aberto: true, mensagem: exception.response.data.message });
        } else {
          setErro(MENSAGEM_ERRO_GENERICA);
        }
      }
    } finally {
      if (sessaoRef.current === sessaoAlvo) {
        enviandoRef.current = false;
        setEnviando(false);
      }
    }
  };

  return (
    <>
      <Dialog open={open} onClose={enviando ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar Termo de Acordo</DialogTitle>
        <DialogContent>
          <Field
            label="Para*"
            placeholder={PLACEHOLDER_EMAIL_LIST}
            value={emailList}
            onChange={onChangeEmailList}
            disabled={enviando}
            error={errorFields.emailList.error}
            helperText={errorFields.emailList.errorMessage}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button onClick={onSubmit} variant="contained" disabled={enviando}>Enviar</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={dialogErro.aberto} onClose={onFecharDialogErro} maxWidth="xs" fullWidth>
        <DialogContent>
          <DialogContentText>{dialogErro.mensagem}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onFecharDialogErro}>Ok, ENTENDI</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={onFecharErro}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={onFecharErro}>
          {erro}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(resultado)}
        autoHideDuration={4000}
        onClose={onFecharResultado}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={resultado?.severidade ?? 'info'} variant="filled" onClose={onFecharResultado}>
          {resultado?.mensagem}
        </Alert>
      </Snackbar>
    </>
  );
}

DialogEnviarEmailAcordo.propTypes = {
  open: PropTypes.bool,
  acordo: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired,
  onMutado: PropTypes.func,
};

DialogEnviarEmailAcordo.defaultProps = {
  open: false,
  acordo: null,
  idProcesso: null,
  onMutado: null,
};
